/**
 * Spanish Privacy Policy translations
 */
export const privacy = {
  pageTitle: "Política de Privacidad",
  heroSubtitle: "Su privacidad es importante para nosotros. Esta política explica cómo recopilamos, usamos y protegemos su información personal.",
  lastUpdated: "Última Actualización: Enero de 2026",
  effectiveDate: "Vigencia: 1 de Enero de 2026",

  intro: {
    title: "Introducción",
    welcome: "Bienvenido al Observatorio de Futuros de la Juventud de los Consejos de Desarrollo de JCI. Estamos comprometidos a proteger su privacidad y garantizar transparencia sobre cómo manejamos sus datos.",
    commitment: "Esta Política de Privacidad explica qué información personal recopilamos, cómo la usamos y sus derechos con respecto a sus datos. Seguimos los principios de minimización de datos y limitación de propósito.",
    scope: "Esta política se aplica a todos los usuarios de la plataforma del Observatorio de Futuros de la Juventud, incluidos los miembros de JCI (Junior Chamber International) y visitantes públicos.",
  },

  controller: {
    title: "Controlador de Datos",
    description: "El controlador de datos responsable de su información personal es:",
    name: "Consejos de Desarrollo de JCI",
    contact: "Para consultas relacionadas con la privacidad, contacte:",
  },

  collection: {
    title: "Información que Recopilamos",
    description: "Recopilamos diferentes tipos de información según cómo interactúa con nuestra plataforma:",
    account: {
      title: "Información de la Cuenta",
      email: "Dirección de correo electrónico (para autenticación y notificaciones)",
      name: "Nombre para mostrar (opcional, para personalización)",
      avatar: "Foto de perfil (opcional, almacenada de forma segura)",
      organization: "Afiliación a la organización JCI (opcional)",
    },
    preferences: {
      title: "Preferencias del Usuario",
      language: "Configuración de idioma preferido",
      theme: "Preferencia de modo claro/oscuro",
      country: "País de enfoque para análisis de datos",
      interest: "Área de interés principal (ej: Salud Mental, Emprendimiento)",
    },
    analytics: {
      title: "Datos de Análisis (con consentimiento)",
      description: "Cuando consiente el análisis, recopilamos:",
      pages: "Páginas visitadas y patrones de navegación",
      scroll: "Profundidad de desplazamiento en páginas de contenido",
      time: "Tiempo dedicado en la plataforma",
      device: "Tipo de navegador y dispositivo (anonimizado)",
    },
  },

  usage: {
    title: "Cómo Usamos Sus Datos",
    description: "Usamos su información para los siguientes propósitos:",
    purpose1: {
      title: "Provisión de Servicios",
      description: "Para autenticarlo, personalizar su experiencia y entregar las funciones del Observatorio que solicita.",
    },
    purpose2: {
      title: "Mejora de la Plataforma",
      description: "Para entender cómo los usuarios interactúan con nuestra plataforma y mejorar la funcionalidad (solo con consentimiento de análisis).",
    },
    purpose3: {
      title: "Comunicaciones",
      description: "Para enviarle actualizaciones importantes sobre la plataforma, si ha optado por recibir notificaciones.",
    },
    purpose4: {
      title: "Seguridad",
      description: "Para proteger contra acceso no autorizado y mantener la integridad de nuestros sistemas.",
    },
  },

  legal: {
    title: "Base Legal para el Procesamiento",
    description: "Bajo el GDPR, procesamos sus datos basándonos en los siguientes fundamentos legales:",
    consent: {
      title: "Consentimiento (Artículo 6(1)(a))",
      description: "Para seguimiento de análisis y comunicaciones de marketing. Puede retirar el consentimiento en cualquier momento.",
    },
    contract: {
      title: "Ejecución de Contrato (Artículo 6(1)(b))",
      description: "Para proporcionar los servicios que ha solicitado al crear una cuenta.",
    },
    legitimate: {
      title: "Intereses Legítimos (Artículo 6(1)(f))",
      description: "Para medidas de seguridad y prevención de fraude, equilibradas con sus derechos.",
    },
    obligation: {
      title: "Obligación Legal (Artículo 6(1)(c))",
      description: "Para cumplir con las leyes y regulaciones aplicables.",
    },
  },

  cookies: {
    title: "Cookies y Almacenamiento Local",
    description: "Usamos cookies y almacenamiento del navegador para mejorar su experiencia:",
    essential: {
      title: "Cookies Esenciales",
      description: "Necesarias para la autenticación y funcionalidad básica. No se pueden desactivar.",
    },
    analytics: {
      title: "Cookies de Análisis (Google Analytics 4)",
      description: "Nos ayudan a entender cómo los visitantes usan nuestro sitio. Solo activas con su consentimiento.",
    },
    preferences: {
      title: "Almacenamiento de Preferencias (localStorage)",
      description: "Almacena sus elecciones de idioma, tema y consentimiento localmente en su navegador.",
    },
    manage: "Puede gestionar sus preferencias de cookies en cualquier momento en",
    manageLink: "Configuración",
  },

  thirdParty: {
    title: "Servicios de Terceros",
    description: "Usamos los siguientes servicios de terceros para operar nuestra plataforma:",
    ga4: "Análisis de sitio web y seguimiento de uso (con consentimiento)",
    lovable: "Infraestructura de backend, autenticación y base de datos",
    osm: "Mapas interactivos que muestran la presencia global de JCI",
    fonts: "Entrega de tipografía (familia de fuentes Plus Jakarta Sans)",
  },

  retention: {
    title: "Retención de Datos",
    description: "Retenemos sus datos solo durante el tiempo necesario para los propósitos descritos:",
    dataType: "Tipo de Datos",
    period: "Período de Retención",
    account: "Datos de Cuenta",
    accountPeriod: "Hasta eliminación de cuenta o 2 años de inactividad",
    analytics: "Datos de Análisis",
    analyticsPeriod: "14 meses (predeterminado de Google Analytics)",
    logs: "Registros de Error",
    logsPeriod: "90 días",
  },

  rights: {
    title: "Sus Derechos de Privacidad (GDPR)",
    description: "Bajo el Reglamento General de Protección de Datos, tiene los siguientes derechos:",
    access: {
      title: "Derecho de Acceso",
      description: "Solicitar una copia de todos los datos personales que tenemos sobre usted.",
    },
    rectification: {
      title: "Derecho de Rectificación",
      description: "Solicitar la corrección de datos personales inexactos o incompletos.",
    },
    erasure: {
      title: "Derecho de Supresión",
      description: "Solicitar la eliminación de sus datos personales ('derecho al olvido').",
    },
    restriction: {
      title: "Derecho de Restricción",
      description: "Solicitar la limitación del procesamiento en ciertas circunstancias.",
    },
    portability: {
      title: "Derecho a la Portabilidad de Datos",
      description: "Recibir sus datos en un formato estructurado y legible por máquina.",
    },
    object: {
      title: "Derecho de Oposición",
      description: "Oponerse al procesamiento basado en intereses legítimos o para marketing directo.",
    },
    exercise: "Para ejercer estos derechos, visite su",
    settingsLink: "página de Configuración",
  },

  international: {
    title: "Cumplimiento Internacional de Privacidad",
    description: "Como una organización global que opera en más de 100 países, estamos comprometidos a respetar las leyes y regulaciones de privacidad locales dondequiera que se encuentren nuestros miembros.",
    commitment: "Si bien utilizamos el GDPR como nuestro estándar base (ya que proporciona las protecciones más completas), también cumplimos con las leyes de privacidad locales aplicables, incluyendo:",
    ukGdpr: "UK GDPR para residentes del Reino Unido",
    lgpd: "LGPD (Ley General de Protección de Datos) para residentes brasileños",
    pipeda: "PIPEDA para residentes canadienses",
    appi: "APPI (Ley de Protección de Información Personal) para residentes japoneses",
    localRights: "Si su país tiene legislación de privacidad específica, puede tener derechos adicionales. Contáctenos para entender cómo se aplican sus leyes locales.",
  },

  children: {
    title: "Privacidad de los Niños",
    description: "El Observatorio de Futuros de la Juventud está diseñado para usuarios de 18 años o más, de acuerdo con los requisitos de membresía de JCI. No recopilamos conscientemente datos de niños menores de 16 años.",
    action: "Si cree que hemos recopilado inadvertidamente datos de un menor, contáctenos inmediatamente en privacy@jci.cc.",
  },

  security: {
    title: "Seguridad de Datos",
    description: "Implementamos medidas de seguridad robustas para proteger sus datos:",
    encryption: "Todos los datos están encriptados en tránsito (HTTPS/TLS) y en reposo",
    access: "Controles de acceso estrictos limitan quién puede ver su información",
    rls: "Seguridad a Nivel de Fila (RLS) asegura que solo pueda acceder a sus propios datos",
    audit: "Auditorías de seguridad regulares y evaluaciones de vulnerabilidad",
  },

  transfers: {
    title: "Transferencias Internacionales de Datos",
    description: "Sus datos pueden ser procesados en países fuera de su residencia, incluidos Estados Unidos y la Unión Europea.",
    safeguards: "Aseguramos que existan salvaguardas apropiadas, incluidas las Cláusulas Contractuales Estándar (SCCs) cuando lo exija la ley.",
  },

  changes: {
    title: "Cambios en esta Política",
    description: "Podemos actualizar esta Política de Privacidad periódicamente para reflejar cambios en nuestras prácticas o requisitos legales.",
    notification: "Los cambios significativos se comunicarán por correo electrónico (si tiene una cuenta) o mediante un aviso destacado en nuestra plataforma. La fecha de 'Última Actualización' en la parte superior indica cuándo se realizaron los cambios.",
  },

  contact: {
    title: "Contáctenos",
    description: "Si tiene preguntas sobre esta Política de Privacidad o desea ejercer sus derechos, contáctenos:",
    email: "Consultas de Privacidad:",
    dpa: "También tiene derecho a presentar una queja ante su autoridad local de protección de datos si cree que se han violado sus derechos.",
  },
} as const;
